<template>
  <Navbar />

  <div class="admin-layout">
    <SaidManuBar />

    <div class="main-content-wrapper">
      <div class="win-alert-page">
    <div class="win-alert-header">
      <div>
        <h2>🔔 Win Alert</h2>
        <p>Monitor cumulative net winning across 1 Minute and 3 Minute games.</p>
      </div>

      <div class="status-pill" :class="{ active: settings.enabled }">
        {{ settings.enabled ? "ALERT ON" : "ALERT OFF" }}
      </div>
    </div>

    <div class="settings-grid">
      <div class="setting-card">
        <div class="setting-title">Win Alert</div>
        <div class="setting-row">
          <span>Monitoring</span>
          <button
            class="toggle"
            :class="{ on: settings.enabled }"
            @click="toggleEnabled"
            :disabled="saving"
          >
            {{ settings.enabled ? "ON" : "OFF" }}
          </button>
        </div>
      </div>

      <div class="setting-card">
        <div class="setting-title">Target Net Win</div>
        <div class="target-input">
          <span>₹</span>
          <input
            v-model.number="settings.target"
            type="number"
            min="1"
            step="1"
            @change="saveSettings"
          />
        </div>
        <small>
          Alert when a player's cumulative net winning reaches this amount.
        </small>
      </div>

      <div class="setting-card">
        <div class="setting-title">Sound</div>
        <div class="setting-row">
          <span>{{ settings.soundEnabled ? "Sound enabled" : "Muted" }}</span>
          <button
            class="toggle"
            :class="{ on: settings.soundEnabled }"
            @click="toggleSound"
            :disabled="saving"
          >
            {{ settings.soundEnabled ? "ON" : "MUTE" }}
          </button>
        </div>
      </div>

      <div class="setting-card">
        <div class="setting-title">Continuous Alarm</div>
        <div class="setting-row">
          <span>
            {{ settings.continuousSound ? "Keeps ringing" : "Single alert" }}
          </span>
          <button
            class="toggle"
            :class="{ on: settings.continuousSound }"
            @click="toggleContinuous"
            :disabled="saving"
          >
            {{ settings.continuousSound ? "ON" : "OFF" }}
          </button>
        </div>
      </div>
    </div>

    <div class="control-bar">
      <div>
        <strong>Session:</strong>
        {{ settings.sessionId || "No active session" }}
      </div>

      <div class="controls">
        <button class="refresh-btn" @click="loadAlerts">
          ↻ Refresh
        </button>

        <button class="reset-btn" @click="resetAlert">
      🔄 Reset Alert
    </button>

    <button class="stop-btn" @click="stopAlarm">
          🔇 Stop Alarm
        </button>
      </div>
    </div>

    <div class="info-box">
      <strong>How the calculation works</strong>
      <span>
        Only net winning is counted. Example: ₹100 bet → ₹190 paid amount =
        ₹90 net winning. ₹190 is not counted.
      </span>
    </div>

    <div class="alerts-card">
      <div class="alerts-heading">
        <div>
          <h3>Live Win Alerts</h3>
          <span>{{ alerts.length }} alert{{ alerts.length === 1 ? "" : "s" }}</span>
        </div>

        <div class="monitor-state">
          <span class="dot" :class="{ live: settings.enabled }"></span>
          {{ settings.enabled ? "Monitoring" : "Paused" }}
        </div>
      </div>

      <div v-if="loading" class="empty-state">
        Loading alerts...
      </div>

      <div v-else-if="alerts.length === 0" class="empty-state">
        No target reached in the current session.
      </div>

      <div v-else class="alert-list">
        <div
          v-for="alert in alerts"
          :key="alert.userId || alert._id"
          class="alert-item"
        >
          <div class="player-icon">👤</div>

          <div class="player-info">
            <strong>{{ alert.userid?.name || alert.userid?.mobile || alert.userid || "-" }}</strong>
            <span>
              {{ alert.gameType || "1 Minute / 3 Minute" }}
            </span>
          </div>

            <div class="win-value">
              <span>1 Minute Net</span>
              <strong>₹{{ formatMoney(alert.oneMinNet || 0) }}</strong>

              <span>3 Minute Net</span>
              <strong>₹{{ formatMoney(alert.threeMinNet || 0) }}</strong>

              <span>Total Net Win</span>
              <strong>₹{{ formatMoney(alert.netWinning) }}</strong>
            </div>

          <div class="target-value">
            <span>Target</span>
            <strong>₹{{ formatMoney(alert.targetAmount || settings.target) }}</strong>
          </div>

          <div class="alert-time">
            {{ formatDate(alert.alertTime || alert.createdAt) }}
          </div>
        </div>
      </div>
    </div>

    <audio ref="alarmAudio" preload="auto">
      <source
        src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA="
        type="audio/wav"
      />
    </audio>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Navbar from "./Navbar.vue";
import SaidManuBar from "./SideMenuBar.vue";

export default {
  name: "WinAlert",

  components: {
    Navbar,
    SaidManuBar,
  },

  data() {
    return {
      settings: {
        enabled: false,
        target: 1000,
        soundEnabled: true,
        continuousSound: false,
        sessionId: "",
      },
      alerts: [],
      loading: false,
      saving: false,
      pollTimer: null,
      lastAlertKey: "",
      audioContext: null,
      alarmTimer: null,
    };
  },

  mounted() {
    const user = localStorage.getItem("user-info");

    if (!user) {
      this.$router.push({ name: "AdminLogin" });
      return;
    }

    this.loadSettings();
  },

  beforeUnmount() {
    this.stopPolling();
    this.stopAlarm();
  },

  methods: {
    adminHeaders() {
      let token = localStorage.getItem("user-info");

      try {
        token = JSON.parse(token);
      } catch (e) {
        // token is already a string
      }

      return {
        "x-token": token,
      };
    },

    async loadSettings() {
      try {
        const response = await axios.get("/admin/win-alert/settings", {
          headers: this.adminHeaders(),
        });

        if (response.data?.success) {
          this.settings = {
            ...this.settings,
            ...response.data.data,
            target: response.data.data.targetAmount ?? this.settings.target,
          };

          if (this.settings.enabled) {
            this.startPolling();
          }
        }
      } catch (error) {
        console.error("Win Alert settings error:", error);
      }
    },

    async saveSettings() {
      this.saving = true;

      try {
        const response = await axios.put(
          "/admin/win-alert/settings",
          {
            enabled: this.settings.enabled,
            targetAmount: Number(this.settings.target),
            muted: !this.settings.soundEnabled,
            continuousSound: this.settings.continuousSound,
          },
          {
            headers: this.adminHeaders(),
          }
        );

        if (response.data?.success) {
          this.settings = {
            ...this.settings,
            ...response.data.data,
          };

          if (this.settings.enabled) {
            this.startPolling();
          } else {
            this.stopPolling();
            this.stopAlarm();
          }
        }
      } catch (error) {
        console.error("Win Alert save error:", error);
      } finally {
        this.saving = false;
      }
    },

    async toggleEnabled() {
      this.settings.enabled = !this.settings.enabled;
      await this.saveSettings();
    },

    async toggleSound() {
      this.settings.soundEnabled = !this.settings.soundEnabled;

      if (!this.settings.soundEnabled) {
        this.stopAlarm();
      }

      await this.saveSettings();
    },

    async toggleContinuous() {
      this.settings.continuousSound = !this.settings.continuousSound;
      await this.saveSettings();
    },

    startPolling() {
      if (this.pollTimer) return;

      this.loadAlerts();

      this.pollTimer = setInterval(() => {
        if (this.settings.enabled) {
          this.loadAlerts();
        }
      }, 10000);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async loadAlerts() {
      if (!this.settings.enabled) return;

      this.loading = true;

      try {
        const response = await axios.get("/admin/win-alert/alerts", {
          headers: this.adminHeaders(),
        });

        if (response.data?.success) {
          const newAlerts = response.data.data || [];

          const previousKeys = new Set(
            this.alerts.map(
              (item) => item.userId || item._id || item.alertAt
            )
          );

          this.alerts = newAlerts;

          const freshAlert = newAlerts.find(
            (item) =>
              !previousKeys.has(
                item.userId || item._id || item.alertAt
              )
          );

          if (freshAlert) {
            this.handleAlertSound();
          }
        }
      } catch (error) {
        console.error("Win Alert polling error:", error);
      } finally {
        this.loading = false;
      }
    },

    handleAlertSound() {
      if (!this.settings.soundEnabled) return;

      this.playBeep();

      if (this.settings.continuousSound) {
        this.startContinuousAlarm();
      }
    },

    playBeep() {
      try {
        const AudioContext =
          window.AudioContext || window.webkitAudioContext;

        if (!AudioContext) return;

        if (!this.audioContext) {
          this.audioContext = new AudioContext();
        }

        const oscillator = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        oscillator.type = "sine";
        oscillator.frequency.value = 880;
        gain.gain.value = 0.15;

        oscillator.connect(gain);
        gain.connect(this.audioContext.destination);

        oscillator.start();

        setTimeout(() => {
          oscillator.stop();
        }, 700);
      } catch (error) {
        console.error("Alarm sound error:", error);
      }
    },

    startContinuousAlarm() {
      if (this.alarmTimer) return;

      this.alarmTimer = setInterval(() => {
        if (
          this.settings.enabled &&
          this.settings.soundEnabled &&
          this.settings.continuousSound
        ) {
          this.playBeep();
        } else {
          this.stopAlarm();
        }
      }, 1500);
    },

    async resetAlert() {
      try {
        await axios.post(
          "/admin/win-alert/reset",
          {},
          {
            headers: this.adminHeaders(),
          }
        );

        this.alerts = [];
        this.stopAlarm();
        await this.loadAlerts();

      } catch (error) {
        console.error("Reset alert API error:", error);
      }
    },

    async stopAlarm() {
      try {
        await axios.post(
          "/admin/win-alert/stop",
          {},
          {
            headers: this.adminHeaders(),
          }
        );
      } catch (error) {
        console.error("Stop alarm API error:", error);
      }

      if (this.alarmTimer) {
        clearInterval(this.alarmTimer);
        this.alarmTimer = null;
      }
    },

    formatMoney(value) {
      const number = Number(value || 0);
      return number.toFixed(2);
    },

    formatDate(value) {
      if (!value) return "-";

      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return "-";
      }
    },
  },
};
</script>

<style scoped>
.win-alert-page {
  padding: 24px;
  min-height: 100vh;
  background: #f5f7fb;
}

.win-alert-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
}

.win-alert-header h2 {
  margin: 0 0 6px;
  font-size: 26px;
}

.win-alert-header p {
  margin: 0;
  color: #687385;
}

.status-pill {
  padding: 9px 16px;
  border-radius: 999px;
  background: #e9edf3;
  color: #657080;
  font-weight: 700;
}

.status-pill.active {
  background: #dff7e8;
  color: #15803d;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 18px;
}

.setting-card,
.alerts-card,
.control-bar,
.info-box {
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 14px;
  box-shadow: 0 3px 14px rgba(20, 30, 50, 0.05);
}

.setting-card {
  padding: 18px;
}

.setting-title {
  font-weight: 700;
  margin-bottom: 15px;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.toggle {
  border: 0;
  border-radius: 999px;
  padding: 8px 15px;
  background: #e7ebf0;
  color: #475569;
  cursor: pointer;
  font-weight: 700;
}

.toggle.on {
  background: #16a34a;
  color: white;
}

.target-input {
  display: flex;
  align-items: center;
  border: 1px solid #d9dee7;
  border-radius: 9px;
  overflow: hidden;
  margin-bottom: 8px;
}

.target-input span {
  padding-left: 11px;
  color: #667085;
}

.target-input input {
  width: 100%;
  border: 0;
  outline: 0;
  padding: 10px;
  font-size: 16px;
}

.setting-card small {
  color: #7a8494;
}

.control-bar {
  padding: 14px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.controls {
  display: flex;
  gap: 10px;
}

.refresh-btn,
.stop-btn {
  border: 0;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
}

.refresh-btn {
  background: #eef2f7;
}

.stop-btn {
  background: #fee2e2;
  color: #b91c1c;
}

.info-box {
  padding: 15px 18px;
  margin-bottom: 18px;
  display: flex;
  gap: 12px;
  flex-direction: column;
}

.info-box span {
  color: #667085;
}

.alerts-card {
  overflow: hidden;
}

.alerts-heading {
  padding: 18px;
  border-bottom: 1px solid #edf0f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.alerts-heading h3 {
  margin: 0 0 5px;
}

.alerts-heading span {
  color: #7a8494;
  font-size: 13px;
}

.monitor-state {
  display: flex;
  align-items: center;
  gap: 7px;
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #9ca3af;
}

.dot.live {
  background: #22c55e;
}

.alert-item {
  display: grid;
  grid-template-columns: 45px 1.5fr 1fr 1fr 1fr;
  align-items: center;
  gap: 14px;
  padding: 15px 18px;
  border-bottom: 1px solid #edf0f4;
}

.player-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2f7;
}

.player-info,
.win-value,
.target-value {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.player-info span,
.win-value span,
.target-value span,
.alert-time {
  color: #7a8494;
  font-size: 13px;
}

.win-value strong {
  color: #15803d;
}

.empty-state {
  padding: 45px;
  text-align: center;
  color: #7a8494;
}

@media (max-width: 1100px) {
  .settings-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }

  .control-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .alert-item {
    grid-template-columns: 40px 1fr;
  }
}
</style>
